import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search, UserX, UserCheck, Wallet } from "lucide-react";

export default function UserListAdmin() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [addFunds, setAddFunds] = useState("");

  // ✅ Fetch Users
  const fetchUsers = async () => {
    try {
      const res = await axios.get("https://api.speedfollowinfo.com/api/admin/users");
      if (res.data.success) {
        setUsers(res.data.users);
      } else {
        alert("❌ Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  useEffect(() => {
    fetchUsers();
    const interval = setInterval(fetchUsers, 15000);
    return () => clearInterval(interval);
  }, []);

  // ✅ Filter Users
  const filteredUsers = users.filter(
    (u) =>
      u.username?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ Toggle user status (Suspend / Activate)
  const toggleStatus = async (userId, newStatus) => {
  try {
    console.log("🟢 Updating user:", userId, "→", newStatus);

    const res = await axios.put(
      `https://api.speedfollowinfo.com/api/admin/user-status/${userId}`,
      { status: newStatus }
    );

    if (res.data.success && res.data.user) {
      // ✅ Update local user list immediately
      setUsers((prev) =>
        prev.map((u) =>
          u._id === userId ? { ...u, status: res.data.user.status } : u
        )
      );

      // ✅ Force refresh to ensure UI + DB in sync
      await fetchUsers();

      // ✅ Notification UI
      const msgBox = document.createElement("div");
      msgBox.textContent =
        newStatus === "active"
          ? "✅ User Activated Successfully!"
          : "🚫 User Suspended Successfully!";
      msgBox.style.position = "fixed";
      msgBox.style.top = "20px";
      msgBox.style.right = "20px";
      msgBox.style.background =
        newStatus === "active" ? "#4ade80" : "#f87171";
      msgBox.style.color = "#0b0f1a";
      msgBox.style.fontWeight = "600";
      msgBox.style.padding = "10px 18px";
      msgBox.style.borderRadius = "8px";
      msgBox.style.boxShadow = "0 0 15px rgba(0,0,0,0.5)";
      msgBox.style.zIndex = "9999";
      document.body.appendChild(msgBox);
      setTimeout(() => msgBox.remove(), 2000);
    } else {
      alert("❌ Failed to update user status.");
    }
  } catch (err) {
    console.error("Status update failed:", err);
    alert("⚠️ Something went wrong!");
  }
};

  // ✅ Add Funds
  const handleAddFunds = async () => {
    if (!selectedUser || !addFunds) return;
    try {
      const res = await axios.post(
        `https://api.speedfollowinfo.com/api/admin/addfund/${selectedUser._id}`,
        { amount: addFunds }
      );
      if (res.data.success) {
        alert("✅ Funds added successfully!");
      }
      setAddFunds("");
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      console.error("Add fund failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0f1a] text-white p-6 md:p-10">
      <h1 className="text-2xl font-bold mb-6">👥 User Management</h1>

      {/* 🔍 Search */}
      <div className="mb-6 flex items-center gap-3 bg-[#111827] px-4 py-2 rounded-lg">
        <Search size={20} className="text-gray-400" />
        <input
          type="text"
          placeholder="Search by username or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-transparent outline-none w-full text-white"
        />
      </div>

      {/* 🧾 Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-[#111827] text-[#f2a900]">
              <th className="p-3 border-b border-gray-700">#</th>
              <th className="p-3 border-b border-gray-700">Username</th>
              <th className="p-3 border-b border-gray-700">Email</th>
              <th className="p-3 border-b border-gray-700">Balance</th>
              <th className="p-3 border-b border-gray-700">Status</th>
              <th className="p-3 border-b border-gray-700 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-6 text-gray-400 italic">
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map((user, index) => (
                <tr
                  key={user._id}
                  className="hover:bg-[#1b2138] transition border-b border-gray-800"
                >
                  <td className="p-3">{index + 1}</td>
                  <td className="p-3 font-semibold">{user.username}</td>
                  <td className="p-3">
  <td className="p-3">
  <div className="flex flex-col">
    <span className="text-white font-medium">
      {user.email}
    </span>

    {user.phone && (
      <span className="text-sm font-semibold text-gray-300 mt-1">
        {user.phone}
      </span>
    )}
  </div>
</td>
</td>
                  <td className="p-3 text-[#ffd700] font-semibold">
                    ৳{user.balance?.toFixed(2)}
                  </td>
                  <td
                    className={`p-3 font-semibold ${
                      user.status === "active"
                        ? "text-green-400"
                        : "text-yellow-400"
                    }`}
                  >
                    {user.status || "active"}
                  </td>

                  {/* 🎯 Actions */}
                  <td className="p-3 flex justify-center gap-3">
                    <button
                      onClick={() => toggleStatus(user._id, "active")}
                      disabled={user.status === "active"}
                      className={`px-3 py-1 rounded-md text-sm font-semibold flex items-center gap-1 ${
                        user.status === "active"
                          ? "bg-green-700/40 text-green-400 cursor-not-allowed"
                          : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                      }`}
                    >
                      <UserCheck size={14} /> Activate
                    </button>

                    <button
                      onClick={() => toggleStatus(user._id, "suspended")}
                      disabled={user.status === "suspended"}
                      className={`px-3 py-1 rounded-md text-sm font-semibold flex items-center gap-1 ${
                        user.status === "suspended"
                          ? "bg-yellow-700/40 text-yellow-400 cursor-not-allowed"
                          : "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30"
                      }`}
                    >
                      <UserX size={14} /> Suspend
                    </button>

                    <button
                      onClick={() => setSelectedUser(user)}
                      className="px-3 py-1 bg-[#6c63ff]/20 text-[#6c63ff] hover:bg-[#6c63ff]/30 rounded-md text-sm font-semibold flex items-center gap-1"
                    >
                      <Wallet size={14} /> Add Funds
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 💰 Add Funds Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#111827] p-6 rounded-xl w-80 shadow-2xl">
            <h2 className="text-xl font-bold mb-3 text-center">💰 Add Funds</h2>
            <p className="text-center mb-4 text-gray-400">
              To:{" "}
              <span className="text-[#ffd700]">{selectedUser.username}</span>
            </p>
            <input
              type="number"
              placeholder="Enter amount"
              value={addFunds}
              onChange={(e) => setAddFunds(e.target.value)}
              className="w-full bg-transparent border border-gray-600 rounded-md px-3 py-2 mb-4 text-white"
            />
            <div className="flex justify-between">
              <button
                onClick={handleAddFunds}
                className="bg-[#6c63ff] px-4 py-2 rounded-md hover:bg-[#5848e2] transition"
              >
                Add
              </button>
              <button
                onClick={() => setSelectedUser(null)}
                className="bg-red-500/20 px-4 py-2 rounded-md text-red-400 hover:bg-red-500/30 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}